import type { PlanetPosition } from '../../types/api';

// Zodiac sign elements (1-indexed by sign number)
type Element = 'Fire' | 'Earth' | 'Air' | 'Water';

const SIGN_ELEMENTS: Record<number, Element> = {
  1: 'Fire',    // Aries
  2: 'Earth',   // Taurus
  3: 'Air',     // Gemini
  4: 'Water',   // Cancer
  5: 'Fire',    // Leo
  6: 'Earth',   // Virgo
  7: 'Air',     // Libra
  8: 'Water',   // Scorpio
  9: 'Fire',    // Sagittarius
  10: 'Earth',  // Capricorn
  11: 'Air',    // Aquarius
  12: 'Water',  // Pisces
};

export const getElementForSign = (signNumber: number): Element => {
  return SIGN_ELEMENTS[signNumber] || 'Fire';
};

export interface ElementCount {
  element: Element;
  count: number;
  planets: string[];
}

export const classifyPlanetsByElement = (planets: PlanetPosition[]): ElementCount[] => {
  const elementMap: Record<Element, string[]> = {
    Fire: [],
    Earth: [],
    Air: [],
    Water: [],
  };

  for (const p of planets) {
    const element = getElementForSign(p.zodiac_sign_number);
    elementMap[element].push(PLANET_SANSKRIT_NAMES[p.planet_english] || p.planet_english);
  }

  return (['Fire', 'Earth', 'Air', 'Water'] as Element[]).map((element) => ({
    element,
    count: elementMap[element].length,
    planets: elementMap[element],
  }));
};

// Gandanta zones: junctions of Water-Fire signs at 0 and 30 degrees
// Cancer(4)/Leo(5), Scorpio(8)/Sagittarius(9), Pisces(12)/Aries(1)
const GANDANTA_BOUNDARIES = [
  { waterSign: 4, fireSign: 5 },   // Cancer → Leo
  { waterSign: 8, fireSign: 9 },   // Scorpio → Sagittarius
  { waterSign: 12, fireSign: 1 },  // Pisces → Aries
];

const GANDANTA_THRESHOLD_DEG = 3.33; // ~3°20' (one Navamsa pada)

export const isInGandanta = (longitudeDegrees: number): boolean => {
  for (const boundary of GANDANTA_BOUNDARIES) {
    // End of water sign (last 3.33 degrees of sign)
    const waterSignEnd = boundary.waterSign * 30;
    const waterGandantaStart = waterSignEnd - GANDANTA_THRESHOLD_DEG;
    if (longitudeDegrees >= waterGandantaStart && longitudeDegrees < waterSignEnd) {
      return true;
    }
    // Start of fire sign (first 3.33 degrees of sign)
    const fireSignStart = (boundary.fireSign - 1) * 30;
    const fireGandantaEnd = fireSignStart + GANDANTA_THRESHOLD_DEG;
    if (longitudeDegrees >= fireSignStart && longitudeDegrees < fireGandantaEnd) {
      return true;
    }
  }
  return false;
};

// Returns gandanta zone boundary longitudes for rendering overlay
export interface GandantaZone {
  startDeg: number;
  endDeg: number;
  label: string;
}

export const getGandantaZones = (): GandantaZone[] => {
  return [
    {
      startDeg: 4 * 30 - GANDANTA_THRESHOLD_DEG,  // ~116.67
      endDeg: 4 * 30 + GANDANTA_THRESHOLD_DEG,     // ~123.33
      label: 'Cancer-Leo',
    },
    {
      startDeg: 8 * 30 - GANDANTA_THRESHOLD_DEG,   // ~236.67
      endDeg: 8 * 30 + GANDANTA_THRESHOLD_DEG,     // ~243.33
      label: 'Scorpio-Sagittarius',
    },
    {
      startDeg: 360 - GANDANTA_THRESHOLD_DEG,       // ~356.67
      endDeg: GANDANTA_THRESHOLD_DEG,                // ~3.33
      label: 'Pisces-Aries',
    },
  ];
};

export interface MoonPhaseInfo {
  angle: number;          // Sun-Moon elongation 0-360
  phaseName: string;
  illumination: number;   // 0 to 1
  isWaxing: boolean;
}

export const computeMoonPhase = (sunLongitude: number, moonLongitude: number): MoonPhaseInfo => {
  let angle = moonLongitude - sunLongitude;
  if (angle < 0) angle += 360;

  const isWaxing = angle < 180;
  // Illumination: 0 at new moon (0°), 1 at full moon (180°)
  const illumination = (1 - Math.cos((angle * Math.PI) / 180)) / 2;

  let phaseName: string;
  if (angle < 12) phaseName = 'New Moon';
  else if (angle < 90) phaseName = 'Waxing Crescent';
  else if (angle < 100) phaseName = 'First Quarter';
  else if (angle < 168) phaseName = 'Waxing Gibbous';
  else if (angle < 192) phaseName = 'Full Moon';
  else if (angle < 270) phaseName = 'Waning Gibbous';
  else if (angle < 280) phaseName = 'Last Quarter';
  else if (angle < 348) phaseName = 'Waning Crescent';
  else phaseName = 'New Moon';

  return { angle, phaseName, illumination, isWaxing };
};

export interface ConjunctionGroup {
  planets: PlanetPosition[];
  averageLongitude: number;
}

export const detectConjunctions = (
  planets: PlanetPosition[],
  thresholdDeg: number = 10,
): ConjunctionGroup[] => {
  // Sort by longitude
  const sorted = [...planets].sort((a, b) => a.longitude_degrees - b.longitude_degrees);
  const used = new Set<number>();
  const groups: ConjunctionGroup[] = [];

  for (let i = 0; i < sorted.length; i++) {
    if (used.has(i)) continue;
    const group: PlanetPosition[] = [sorted[i]];
    used.add(i);

    for (let j = i + 1; j < sorted.length; j++) {
      if (used.has(j)) continue;
      // Check angular distance (handle 360° wrap)
      let diff = Math.abs(sorted[j].longitude_degrees - sorted[i].longitude_degrees);
      if (diff > 180) diff = 360 - diff;
      if (diff <= thresholdDeg) {
        group.push(sorted[j]);
        used.add(j);
      }
    }

    if (group.length >= 2) {
      const avg = group.reduce((sum, p) => sum + p.longitude_degrees, 0) / group.length;
      groups.push({ planets: group, averageLongitude: avg });
    }
  }

  return groups;
};

// Classical yuti: shared rāśi (co-tenancy), not degree proximity.
export interface YutiGroup {
  planets: PlanetPosition[];
  signNumber: number;
  avgLongitude: number;  // average longitude within the rāśi, for wheel label positioning only
}

export const detectYutis = (planets: PlanetPosition[]): YutiGroup[] => {
  const rashiGroups = new Map<number, PlanetPosition[]>();
  for (const p of planets) {
    const sn = p.zodiac_sign_number;
    if (!rashiGroups.has(sn)) rashiGroups.set(sn, []);
    rashiGroups.get(sn)!.push(p);
  }
  const groups: YutiGroup[] = [];
  Array.from(rashiGroups.entries()).forEach(([signNumber, group]) => {
    if (group.length < 2) return;
    const avg = group.reduce((sum, p) => sum + p.longitude_degrees, 0) / group.length;
    groups.push({ planets: group, signNumber, avgLongitude: avg });
  });
  return groups;
};

// Element colors for rendering zodiac segments
export const ELEMENT_COLORS: Record<Element, { fill: string; stroke: string; text: string }> = {
  Fire: { fill: 'rgba(220, 60, 30, 0.30)', stroke: '#dc3c1e', text: '#ff9a85' },
  Earth: { fill: 'rgba(76, 140, 50, 0.30)', stroke: '#4c8c32', text: '#a3e07a' },
  Air: { fill: 'rgba(80, 160, 220, 0.30)', stroke: '#50a0dc', text: '#a8dbff' },
  Water: { fill: 'rgba(50, 80, 180, 0.30)', stroke: '#3250b4', text: '#96b0ff' },
};

// NASA-style planet rendering colors (hex for SVG)
export const PLANET_COLORS: Record<string, string> = {
  Sun: '#FFB300',
  Moon: '#C0C0C0',
  Mars: '#E53935',
  Mercury: '#66BB6A',
  Jupiter: '#FFD54F',
  Venus: '#F48FB1',
  Saturn: '#5C6BC0',
  Rahu: '#78909C',
  Ketu: '#A1887F',
};

// Planet abbreviations (English)
export const PLANET_ABBREVIATIONS: Record<string, string> = {
  Sun: 'Su',
  Moon: 'Mo',
  Mars: 'Ma',
  Mercury: 'Me',
  Jupiter: 'Ju',
  Venus: 'Ve',
  Saturn: 'Sa',
  Rahu: 'Ra',
  Ketu: 'Ke',
};

// Sanskrit planet abbreviations (for planet circles on the wheel)
// Keys match planet_english from API; values are short Sanskrit labels
export const PLANET_SANSKRIT_ABBR: Record<string, string> = {
  Sun: 'Sū',
  Moon: 'Ca',
  Mars: 'Ku',
  Mercury: 'Bu',
  Jupiter: 'Gu',
  Venus: 'Śk',
  Saturn: 'Śa',
  Rahu: 'Rā',
  Ketu: 'Ke',
};

// Full Sanskrit planet names (for display labels)
export const PLANET_SANSKRIT_NAMES: Record<string, string> = {
  Sun: 'Sūrya',
  Moon: 'Candra',
  Mars: 'Kuja',
  Mercury: 'Budha',
  Jupiter: 'Guru',
  Venus: 'Śukra',
  Saturn: 'Śani',
  Rahu: 'Rāhu',
  Ketu: 'Ketu',
};

// Zodiac Unicode symbols
export const ZODIAC_SYMBOLS: Record<number, string> = {
  1: '\u2648',  // Aries ♈
  2: '\u2649',  // Taurus ♉
  3: '\u264A',  // Gemini ♊
  4: '\u264B',  // Cancer ♋
  5: '\u264C',  // Leo ♌
  6: '\u264D',  // Virgo ♍
  7: '\u264E',  // Libra ♎
  8: '\u264F',  // Scorpio ♏
  9: '\u2650',  // Sagittarius ♐
  10: '\u2651', // Capricorn ♑
  11: '\u2652', // Aquarius ♒
  12: '\u2653', // Pisces ♓
};

// Zodiac sign names (English)
export const ZODIAC_NAMES: Record<number, string> = {
  1: 'Aries',
  2: 'Taurus',
  3: 'Gemini',
  4: 'Cancer',
  5: 'Leo',
  6: 'Virgo',
  7: 'Libra',
  8: 'Scorpio',
  9: 'Sagittarius',
  10: 'Capricorn',
  11: 'Aquarius',
  12: 'Pisces',
};

// Zodiac sign Sanskrit names (matching backend planetary_positions_manager.py)
export const ZODIAC_SANSKRIT_NAMES: Record<number, string> = {
  1: 'Meṣa',
  2: 'Vṛṣabha',
  3: 'Mithuna',
  4: 'Karkaṭa',
  5: 'Siṃha',
  6: 'Kanyā',
  7: 'Tulā',
  8: 'Vṛścika',
  9: 'Dhanus',
  10: 'Makara',
  11: 'Kumbha',
  12: 'Mīna',
};

// Nakshatra data — 27 nakshatras, each spanning 13°20' (13.333°)
// Names sourced from nakshatra_properties.json; shortName used on the wheel
export interface NakshatraInfo {
  name: string;       // full Sanskrit name from JSON
  shortName: string;  // abbreviated for tight wheel rendering
  startDeg: number;
}

export const NAKSHATRA_DATA: NakshatraInfo[] = [
  { name: 'Aśvinī',          shortName: 'Aśvinī',       startDeg: 0 },
  { name: 'Bharaṇī',         shortName: 'Bharaṇī',      startDeg: 13.333 },
  { name: 'Kṛttikā',         shortName: 'Kṛttikā',      startDeg: 26.667 },
  { name: 'Rohiṇī',          shortName: 'Rohiṇī',       startDeg: 40 },
  { name: 'Mṛgaśirā',       shortName: 'Mṛgaśirā',    startDeg: 53.333 },
  { name: 'Ārdrā',           shortName: 'Ārdrā',        startDeg: 66.667 },
  { name: 'Punarvasu',       shortName: 'Punarvasu',    startDeg: 80 },
  { name: 'Puṣya',           shortName: 'Puṣya',        startDeg: 93.333 },
  { name: 'Āśleṣā',          shortName: 'Āśleṣā',       startDeg: 106.667 },
  { name: 'Maghā',           shortName: 'Maghā',        startDeg: 120 },
  { name: 'Pūrvaphalgunī',   shortName: 'P.Phalgunī',   startDeg: 133.333 },
  { name: 'Uttaraphalgunī',  shortName: 'U.Phalgunī',   startDeg: 146.667 },
  { name: 'Hasta',           shortName: 'Hasta',         startDeg: 160 },
  { name: 'Citrā',           shortName: 'Citrā',         startDeg: 173.333 },
  { name: 'Svāti',           shortName: 'Svāti',         startDeg: 186.667 },
  { name: 'Viśākhā',         shortName: 'Viśākhā',      startDeg: 200 },
  { name: 'Anūrādhā',        shortName: 'Anūrādhā',     startDeg: 213.333 },
  { name: 'Jyeṣṭhā',        shortName: 'Jyeṣṭhā',      startDeg: 226.667 },
  { name: 'Mūlā',            shortName: 'Mūlā',         startDeg: 240 },
  { name: 'Pūrvāṣāḍhā',     shortName: 'P.Āṣāḍhā',    startDeg: 253.333 },
  { name: 'Uttarāṣāḍhā',    shortName: 'U.Āṣāḍhā',    startDeg: 266.667 },
  { name: 'Śravaṇā',         shortName: 'Śravaṇā',      startDeg: 280 },
  { name: 'Dhaniṣṭhā',       shortName: 'Dhaniṣṭhā',    startDeg: 293.333 },
  { name: 'Śatabhiṣā',       shortName: 'Śatabhiṣā',    startDeg: 306.667 },
  { name: 'Pūrvābhādrā',     shortName: 'P.Bhādrā',     startDeg: 320 },
  { name: 'Uttarābhādrā',    shortName: 'U.Bhādrā',     startDeg: 333.333 },
  { name: 'Revatī',          shortName: 'Revatī',        startDeg: 346.667 },
];

export type { Element };
